import * as React from 'react'
// libs
import { arrayPush } from 'redux-form'
import { useDispatch } from 'react-redux'
import { useApolloClient } from 'hooks/useApolloClient'
// graphql
import { RISKS_QUERY } from 'graphql/risk/queries'
// types
import { IField, IFieldLink, IFieldValue, IFieldValueMapItem, IRisk, IRiskTemplate } from 'types/graphql'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// constants
import { Forms } from 'constants/index'
// hooks
import { useCurrentTable } from 'hooks/risks'
// utils
import { get } from 'lodash'
import { handleError } from 'utils/errors/handlers'
import { generateRowInitialValue } from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Table/components/TableForm/utils'
import { getFieldsInitialValues, isLinked } from 'utils/form'

interface IContext {
  field: IField & any
  linked: boolean
  addRow: (fieldName: string, parentFieldValueId?: string) => (risk?: IRisk) => Promise<void>
  fieldLink: IFieldLink
  formRiskTemplate: IRiskTemplate
  primaryFieldLink: IFieldLink
}

interface IProps {
  field: IField
  children?: React.ReactNode
}

export const RiskFormContext = React.createContext<IContext>({} as any)

const RiskFormProvider: React.FC<IProps> = ({ children, field }) => {
  const dispatch = useDispatch()

  const { client } = useApolloClient()

  const { risk: currentRisk } = React.useContext(RiskAPIContext)
  const { riskTemplate, linkedRiskTemplates, updateFieldValues } = React.useContext(RiskTemplateAPIContext)

  const { fieldLinks: tableFieldLinks } = useCurrentTable()

  const fieldLink = riskTemplate.fieldLinks.find(
    ({ field: { id }, parentFieldId }) => id === field.id && parentFieldId === field.parentFieldId
  )

  const primaryFieldLink = tableFieldLinks.find(
    ({ field: { entityId }, primary }) => primary && entityId === field.entityId
  )

  const linked = isLinked(fieldLink?.linkingSettings) || isLinked(primaryFieldLink?.linkingSettings)

  const allFields = tableFieldLinks.filter((item) => item.field.entityId === fieldLink?.field.entityId)
  const secondaryFields = allFields.filter((item) => !item.primary)
  const formRiskTemplate = linkedRiskTemplates.find((item) => item.id === fieldLink.field.entityId)

  const dispatchRows = (rowsMap: [string, any][]) =>
    rowsMap.forEach(([name, value]) => dispatch(arrayPush(Forms.RISK_TEMPLATE_FORM, name, value)))

  const addSecondaryLinkedRow: IContext['addRow'] = (fieldName, parentFieldValueId) => async () => {
    try {
      // in the risk template mode there is no risk — parent values are stored on the template itself
      const parentFieldValueMap: IFieldValueMapItem[] = currentRisk?.fieldValueMap ?? riskTemplate?.fieldValueMap ?? []

      const riskId = parentFieldValueMap.find((item) => item.fieldValueId === parentFieldValueId)?.fieldValue?.value

      if (!riskId) {
        console.log('[RiskFormProvider.addSecondaryLinkedRow]: linked risk is not found for ', parentFieldValueId)

        return
      }

      const linkedRisk: IRisk = get(
        await client.query({
          query: RISKS_QUERY,
          variables: {
            filter: {
              ids: [riskId],
              riskTemplateId: primaryFieldLink.field.entityId
            }
          }
        }),
        'data.risks.data[0]'
      )

      const linkedValue = { value: linkedRisk?.id, entity: 'LINKED_RISK' }

      let linkedFieldValueId: string = linkedRisk?.fieldValueMap.find(
        (item: IFieldValueMapItem) => item.fieldId === fieldLink.field.id
      )?.fieldValueId

      if (!linkedFieldValueId) {
        linkedFieldValueId = get(
          await updateFieldValues(
            [
              {
                value: generateRowInitialValue(fieldLink.field.fieldType),
                fieldId: fieldLink.field.id
              }
            ],
            riskId
          ),
          'data.fieldValueUpsert[0].id'
        )
      }

      const fieldValue: IFieldValue = get(
        await updateFieldValues([
          {
            value: {
              ...linkedValue,
              linkedFieldValueId
            },
            fieldId: fieldLink.field.id,
            fieldLinkId: fieldLink.id,
            parentFieldValueId
          }
        ]),
        'data.fieldValueUpsert[0]'
      )

      const initialValues =
        (await getFieldsInitialValues({
          client,
          isRisk: true,
          fields: [fieldLink.field],
          fieldValues: [fieldValue?.linkedFieldValue]
        })) || {}

      const value = {
        value: initialValues[fieldLink.field.id]?.value,
        fieldValueId: fieldValue?.id,
        linkedRiskId: linkedValue.value,
        linkedFieldValueId: fieldValue?.linkedFieldValue?.fieldValueId,
        parentFieldValueId
      }

      dispatch(arrayPush(Forms.RISK_TEMPLATE_FORM, fieldName, value))
    } catch (e) {
      handleError(e, 'RiskFormProvider.addSecondaryRow')
    }
  }

  const addLinkedRow: IContext['addRow'] = (fieldName, parentFieldValueId) => async (risk) => {
    try {
      if (!risk) {
        return
      }

      const linkedValue = { value: risk.id, entity: 'LINKED_RISK' }
      const fieldsWithData = risk.fieldValueMap.map((item) => item.fieldId)
      const fieldsToCreate = allFields.filter(({ field: { id } }) => !fieldsWithData.includes(id))

      let fieldValuesToLink = [...risk.fieldValueMap]

      if (fieldsToCreate.length) {
        const linkedFieldValues: IFieldValue[] = get(
          await updateFieldValues(
            fieldsToCreate.map((item) => ({
              value: generateRowInitialValue(item.field.fieldType),
              fieldId: item.field.id
            })),
            risk.id
          ),
          'data.fieldValueUpsert',
          []
        )

        fieldValuesToLink = [
          ...fieldValuesToLink,
          ...linkedFieldValues.map((item) => ({
            fieldId: item.fieldId,
            fieldValueId: item.id
          }))
        ]
      }

      const primaryFieldValue: IFieldValue = get(
        await updateFieldValues([
          {
            value: {
              ...linkedValue,
              linkedFieldValueId: fieldValuesToLink.find(({ fieldId }) => fieldId === field.id)?.fieldValueId
            },
            fieldId: field.id,
            fieldLinkId: fieldLink.id,
            parentFieldValueId
          }
        ]),
        'data.fieldValueUpsert[0]'
      )

      let secondaryFieldValues: IFieldValue[] = []

      if (secondaryFields.length) {
        secondaryFieldValues = get(
          await updateFieldValues(
            secondaryFields.map((item) => ({
              value: {
                ...linkedValue,
                linkedFieldValueId: fieldValuesToLink.find(({ fieldId }) => fieldId === item.field.id)?.fieldValueId
              },
              fieldId: item.field.id,
              fieldLinkId: item.id,
              parentFieldValueId: primaryFieldValue?.id
            }))
          ),
          'data.fieldValueUpsert',
          []
        )
      }

      const linkedFields = riskTemplate.fields.filter((item) => item.entityId === fieldLink.field.entityId)
      const fieldValues = [primaryFieldValue, ...secondaryFieldValues]

      const initialValues =
        (await getFieldsInitialValues({
          client,
          isRisk: true,
          fields: linkedFields,
          fieldValues: fieldValues.map((item) => item.linkedFieldValue).filter(Boolean)
        })) || {}

      const rowsMap: [string, any][] = allFields.map((item) => {
        const name = item.primary ? fieldName : `${item.id}[${primaryFieldValue.id}]`

        const fieldValue = fieldValues.find((fieldValue) => fieldValue.fieldId === item.field.id)

        return [
          name,
          {
            value: initialValues[item.field.id]?.value,
            fieldValueId: fieldValue?.id,
            linkedRiskId: linkedValue.value,
            linkedFieldValueId: fieldValue?.linkedFieldValue?.fieldValueId,
            parentFieldValueId: item.primary ? parentFieldValueId : primaryFieldValue.id
          }
        ]
      })

      dispatchRows(rowsMap)
    } catch (e) {
      handleError(e, 'RiskFormProvider.addLinkedRow')
    }
  }

  const addRow: IContext['addRow'] =
    (fieldName, parentFieldValueId = null) =>
    async () => {
      const initialValue = formRiskTemplate?.fieldValueMap.find(
        (item) => item.fieldId === fieldLink.field.id
      )?.fieldValue

      const primaryFieldValue: IFieldValue = get(
        await updateFieldValues(
          [
            {
              value: initialValue ?? generateRowInitialValue(field.fieldType),
              fieldId: field.id,
              fieldLinkId: fieldLink.id,
              parentFieldValueId
            }
          ],
          null,
          field
        ),
        'data.fieldValueUpsert[0]'
      )

      let secondaryFieldValues: IFieldValue[] = []

      if (secondaryFields.length) {
        secondaryFieldValues = get(
          await updateFieldValues(
            secondaryFields.map((item) => {
              const initialValue = formRiskTemplate?.fieldValueMap.find(
                ({ fieldId }) => fieldId === item.field.id
              )?.fieldValue

              return {
                value: initialValue ?? generateRowInitialValue(item.field.fieldType),
                fieldId: item.field.id,
                fieldLinkId: item.id,
                parentFieldValueId: primaryFieldValue?.id
              }
            })
          ),
          'data.fieldValueUpsert',
          []
        )
      }

      const linkedFields = riskTemplate.fields.filter((item) => item.entityId === fieldLink.field.entityId)
      const fieldValues = [primaryFieldValue, ...secondaryFieldValues]

      const initialValues =
        (await getFieldsInitialValues({
          client,
          isRisk: true,
          fields: linkedFields,
          fieldValues: fieldValues
            .map((item) => {
              const field = riskTemplate.fields.find(({ id }) => id === item.fieldId)

              return {
                fieldValueId: item.id,
                fieldValue: item.value,
                fieldName: field.name,
                fieldType: field.fieldType
              }
            })
            .filter(Boolean)
        })) || {}

      const rowsMap: [string, any][] = allFields.map((item) => {
        const name = item.primary ? fieldName : `${item.id}[${primaryFieldValue.id}]`
        const fieldValue = fieldValues.find((fieldValue) => fieldValue.fieldId === item.field.id)

        return [
          name,
          {
            value: initialValues[item.field.id]?.value,
            fieldValueId: fieldValue?.id,
            parentFieldValueId: item.primary ? parentFieldValueId : primaryFieldValue.id
          }
        ]
      })

      dispatchRows(rowsMap)
    }

  const addSecondaryRow: IContext['addRow'] = (fieldName, parentFieldValueId) => async () => {
    const initialValue = formRiskTemplate?.fieldValueMap.find((item) => item.fieldId === fieldLink.field.id)?.fieldValue

    const fieldValue: IFieldValue = get(
      await updateFieldValues(
        [
          {
            value: initialValue ?? generateRowInitialValue(field.fieldType),
            fieldId: field.id,
            fieldLinkId: fieldLink.id,
            parentFieldValueId
          }
        ],
        null,
        field
      ),
      'data.fieldValueUpsert[0]'
    )

    const initialValues =
      (await getFieldsInitialValues({
        client,
        isRisk: true,
        fields: [field],
        fieldValues: [
          {
            fieldValueId: fieldValue?.id,
            fieldValue: fieldValue?.value,
            fieldName: field.name,
            fieldType: field.fieldType
          }
        ].filter(Boolean)
      })) || {}

    dispatch(
      arrayPush(Forms.RISK_TEMPLATE_FORM, fieldName, {
        value: initialValues[fieldLink.field.id]?.value,
        fieldValueId: fieldValue?.id,
        parentFieldValueId
      })
    )
  }

  const value = {
    field,
    linked,
    fieldLink,
    primaryFieldLink,
    formRiskTemplate,
    addRow: linked
      ? fieldLink.primary
        ? addLinkedRow
        : addSecondaryLinkedRow
      : fieldLink.primary
        ? addRow
        : addSecondaryRow
  }

  return <RiskFormContext.Provider value={value}>{children}</RiskFormContext.Provider>
}

export default RiskFormProvider
